/**
 * 「ログ」タブ。この人のアクセスログの直近分。全部はログ画面(ユーザーで絞った状態)で。
 * 機能 `idp.logs` が無い契約ではタブごと出さない(`UserDetailPage`)。
 */
import { Alert, Button, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Link } from "react-router-dom";

import type { IdpUserDetail, LogEvent } from "../../../lib/api";
import { jst } from "../../../lib/format";
import { Section, StatusTag } from "../../../ui";
import { errorText } from "../hooks";
import { logParams, useLogAccess } from "../logHooks";

const LIMIT = 20;

export function LogsTab({ user }: { user: IdpUserDetail }) {
  const params = logParams({ user_id: user.id, limit: LIMIT, offset: 0 });
  const query = useLogAccess(params);
  const href =
    `/idp/logs?tab=access&userId=${encodeURIComponent(user.id)}` +
    `&username=${encodeURIComponent(user.username)}`;

  const columns: ColumnsType<LogEvent> = [
    { title: "日時", dataIndex: "occurredAt", width: 180, render: (v: string) => jst(v) },
    { title: "イベント", dataIndex: "eventTypeLabel", width: 160, },
    {
      title: "結果",
      dataIndex: "resultLabel",
      width: 110,
      render: (v: string, row) => (
        <StatusTag tone={row.result === "success" ? "success" : "danger"}>{v}</StatusTag>
      ),
    },
    { title: "IP", dataIndex: "ip", width: 360, className: "ui-num" },
    { title: "SSO TARGET", width: 240, dataIndex: "target", },
  ];

  return (
    <Section
      title={`アクセスログ(直近 ${LIMIT} 件)`}
      flush
      extra={
        <Link to={href}>
          <Button size="small">ログ画面で全部を見る</Button>
        </Link>
      }
    >
      {query.isError && (
        <Alert type="error" showIcon message="アクセスログを取得できなかった" description={errorText(query.error)} />
      )}
      <Table<LogEvent>
        rowKey="id"
        size="small"
        loading={query.isFetching}
        columns={columns}
        dataSource={query.data?.items ?? []}
        pagination={false}
        // 取得失敗と 0 件は別の文言(「無い」と見せない)
        locale={{ emptyText: query.isError ? "取得できなかったので表示できない" : "この期間のアクセスログは無い" }}
        scroll={{ x: "max-content" }}
      />
    </Section>
  );
}
